/**
 * 与服务端 REST 接口的薄封装。
 *
 * 约定（与 SPEC 一致）：
 *   · 所有接口在 /api 下，返回 JSON
 *   · 未匹配的 /api 路径由服务端兜底返回 404 JSON（不会掉进 SPA 回退拿到 HTML）
 *   · 写操作带 If-Match: <Rev> 做乐观锁，冲突时服务端返回 409（看板接口实现后启用）
 *   · 认证走 Cookie（friday_sid）+ 双提交 CSRF：token 只存内存（api/auth.ts），
 *     写操作由本文件的 request() 自动带 `X-CSRF-Token` 头
 */

import { getCsrfToken, notifyUnauthorized } from './auth'

export interface Health {
  ok: boolean
  service: string
  version: string
  schema_version: string
  /** Unix 秒 */
  time: number
}

/** 设置项的编辑控件类型（服务端新契约字段 `kind`） */
export type SettingKind = 'text' | 'number' | 'select' | 'json-textarea' | 'readonly'

/** 下拉选项：服务端可能给字符串数组，也可能给 {value,label} 对象数组 */
export type SettingOption = string | { value: string; label: string }

/** 设置项。形状已定，具体条目待定 —— 见服务端 SettingsService.h */
export interface SettingItem {
  key: string
  label: string
  type: 'string' | 'password' | 'bool' | 'int' | 'path'
  value: string
  writable: boolean
  /**
   * 编辑控件类型。新的三张卡片（账号 / 上传与附件 / 安全）用它；
   * 老接口只有 `type`，前端会把 `type` 映射成等价的 kind（见 SettingsPage）。
   */
  /** 该条目保存时走哪个接口：profile = PATCH /api/auth/profile，默认走 settings */
  action?: 'settings' | 'profile'
  kind?: SettingKind
  /** 下拉选项（kind='select' 时用） */
  options?: SettingOption[]
  /** 该项的说明文案 */
  hint?: string
}

/** 一个设置分区（一张卡片） */
export interface SettingSection {
  id: string
  title: string
  items: SettingItem[]
  /** 卡片级说明（可选） */
  hint?: string
}

export interface SettingsResponse {
  sections: SettingSection[]
  /** 服务端给出的说明（当前用于标注"接口已预留"） */
  note?: string
  /** 界面偏好（不是设置项，不出现在 sections 里） */
  prefs?: { boardDrawerOpen?: boolean }
}

/** 统一的接口错误；尽量带上服务端返回的 message 与整个 JSON 体 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    message?: string,
    /** 服务端返回的 JSON 体（409 冲突时用它拿 current_rev） */
    readonly payload?: unknown,
  ) {
    super(message ?? `${url} -> HTTP ${status}`)
    this.name = 'ApiError'
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const isWrite = method !== 'GET' && method !== 'HEAD'

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  }
  // 写操作自动带 CSRF 双提交头；token 只存在于内存（见 api/auth.ts）
  if (isWrite) {
    const token = getCsrfToken()
    if (token !== '') headers['X-CSRF-Token'] = token
  }

  const res = await fetch(url, {
    ...init,
    // 会话是 HttpOnly Cookie：不带这个参数，跨源/同源判定会拿不到 Cookie
    credentials: 'same-origin',
    headers,
  })

  if (!res.ok) {
    // 会话失效：通知 AuthContext 清空用户态并跳登录页。
    // 认证接口自身的 401 不触发跳转（登录/改密码失败不能把用户踢走）。
    if (res.status === 401 && !url.startsWith('/api/auth')) notifyUnauthorized()
    // 服务端错误也是 JSON（含 message / error），能取到就展示给用户
    let serverMessage = ''
    let payload: unknown = undefined
    try {
      const body = (await res.json()) as { message?: string; error?: string }
      payload = body
      serverMessage = body.message ?? body.error ?? ''
    } catch {
      // 非 JSON 响应（例如 404 的 HTML），忽略
    }
    throw new ApiError(res.status, url, serverMessage || undefined, payload)
  }

  return (await res.json()) as T
}

export function getHealth(): Promise<Health> {
  return request<Health>('/api/health')
}

export function getSettings(): Promise<SettingsResponse> {
  return request<SettingsResponse>('/api/settings')
}

/** 更新一项设置（旧接口，逐项提交）。服务端存储落地前会返回 501。 */
export function patchSetting(key: string, value: string): Promise<unknown> {
  return request<unknown>('/api/settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value }),
  })
}

/**
 * 批量更新设置（新接口，一张卡片一次提交）。
 *
 * body: `{ "changes": { "<key>": "<value>" } }`
 * 成功 `{ok:true}`；校验失败 400 + `{error:"<code>"}`（如 invalid_max_bytes）。
 */
/**
 * 保存设置。
 *
 * 服务端契约是**一次一个键**：`PATCH /api/settings {key, value}`
 * （不是 `{changes:{...}}`），因此这里逐个提交；任一失败即抛出，由调用方提示。
 * 返回所有键的 warning（服务端可能 200 + warning，例如目录不存在）。
 */
export async function patchSettings(changes: Record<string, string>): Promise<{ warnings: string[] }> {
  const warnings: string[] = []
  for (const [key, value] of Object.entries(changes)) {
    const res = await request<{ ok?: boolean; warning?: string }>('/api/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, value }),
    })
    if (res.warning !== undefined && res.warning !== '') warnings.push(res.warning)
  }
  return { warnings }
}

/* ---------------------------------------------------------------------------
 * 看板（只读）
 * ------------------------------------------------------------------------- */

export interface BoardInfo {
  id: number
  workspaceId: number
  name: string
  description: string
  itemNickname: string
  boardColor: string
  pos: number
  viewState: string
  rev: number
  createdAt: string
  updatedAt: string
  archived: boolean
}

export interface ColumnInfo {
  id: number
  key: string
  /** 'name' / 'text' / 'color' / 'numeric' / 'date' / … */
  type: string
  title: string
  width: number
  pos: number
  hidden: boolean
  /** 服务端已把库里的 JSON 文本解析成对象 */
  settings: unknown
  labels: unknown
  isSystem: boolean
}

export interface GroupInfo {
  id: number
  key: string
  title: string
  /** 色**名**（如 'bright-blue'），不是 hex */
  color: string
  pos: number
  collapsed: boolean
}

export interface ItemInfo {
  id: number
  groupId: number
  name: string
  pos: number
  /** ColumnKey -> Value */
  values: Record<string, string>
}

export interface BoardResponse {
  ok: boolean
  board: BoardInfo
  columns: ColumnInfo[]
  groups: GroupInfo[]
  items: ItemInfo[]
  /** M9.8：组尾统计方式（columnKey -> avg/sum/count/min/max），存数据库 */
  footerAgg?: Record<string, string>
  /** M10：看板级 UI 设置（如 columnAlign: left/center/right），存数据库 */
  uiSettings?: Record<string, string>
}

export function getBoard(id: number | string): Promise<BoardResponse> {
  return request<BoardResponse>(`/api/boards/${id}`)
}

export interface CellPatchResponse {
  ok: boolean
  /** 递增后的新 Rev */
  rev: number
  itemId: number
  columnKey: string
  value: string
}

/**
 * 写一个单元格。
 *
 * 乐观锁：必须带上读看板时拿到的 rev，服务端比对 Board.Rev，不一致返回 409。
 * 409 时 `ApiError.payload` 里带 `current_rev`，调用方据此重新拉取看板对齐。
 */
export function patchCell(
  boardId: number | string,
  itemId: number,
  columnKey: string,
  value: string,
  rev: number,
): Promise<CellPatchResponse> {
  return request<CellPatchResponse>(`/api/boards/${boardId}/cells`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      // ETag 形式（带引号）才是合法的 If-Match 值
      'If-Match': `"${rev}"`,
    },
    body: JSON.stringify({ itemId, columnKey, value }),
  })
}

export type ReorderKind = 'item' | 'column' | 'group'

export interface ReorderResponse {
  ok: boolean
  /** 递增后的新 Rev */
  rev: number
  kind: ReorderKind
}

/**
 * 重排 / 跨组移动。
 *
 * 约定：把**拖动后的完整顺序**发上来，服务端按这个顺序把 Pos 重编号为 0,1,2,…
 * （kind='item' 时 groupId 是目标分组，条目会被移入该分组）。
 * 与 patchCell 一样带 If-Match 做乐观锁。
 */
export function reorder(
  boardId: number | string,
  kind: ReorderKind,
  groupId: number,
  order: number[],
  rev: number,
): Promise<ReorderResponse> {
  return request<ReorderResponse>(`/api/boards/${boardId}/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'If-Match': `"${rev}"`,
    },
    body: JSON.stringify({ kind, groupId, order }),
  })
}
export interface ItemMutationResponse {
  ok: boolean
  /** 递增后的新 Rev */
  rev: number
  itemId: number
}

/** 新增条目：追加到目标分组末尾（groupId 省略/0 时由服务端取第一个分组） */
export function createItem(
  boardId: number | string,
  groupId: number,
  name: string,
  rev: number,
): Promise<ItemMutationResponse> {
  return request<ItemMutationResponse>(`/api/boards/${boardId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ name, groupId }),
  })
}

/** 删除条目：服务端同事务里连带删掉它的单元格值 */
export function deleteItem(
  boardId: number | string,
  itemId: number,
  rev: number,
): Promise<ItemMutationResponse> {
  return request<ItemMutationResponse>(`/api/boards/${boardId}/items`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ itemId }),
  })
}
// ---- M2：分组与列的增删改 ----

export interface StructureResponse {
  ok: boolean
  rev: number
  /** 新建时返回新对象 id */
  id?: number
}

function structureCall(
  boardId: number | string,
  kind: 'groups' | 'columns',
  method: 'POST' | 'PATCH' | 'DELETE',
  body: unknown,
  rev: number,
): Promise<StructureResponse> {
  return request<StructureResponse>(`/api/boards/${boardId}/${kind}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify(body),
  })
}

export const createGroup = (b: number | string, rev: number, title: string, color?: string) =>
  structureCall(b, 'groups', 'POST', { title, color }, rev)

export const updateGroup = (b: number | string, rev: number, groupId: number, patch: { title?: string; color?: string; collapsed?: boolean }) =>
  structureCall(b, 'groups', 'PATCH', { groupId, ...patch }, rev)

export const deleteGroup = (b: number | string, rev: number, groupId: number) =>
  structureCall(b, 'groups', 'DELETE', { groupId }, rev)

export const createColumn = (
  b: number | string, rev: number,
  col: {
    key: string; title: string; type: string; width?: number; afterColumnId?: number
    /** 序列化后的 JSON 文本（复制列时带上原列配置） */
    labels?: string; settings?: string
  },
) => structureCall(b, 'columns', 'POST', col, rev)

export const updateColumn = (b: number | string, rev: number, columnId: number, patch: { title?: string; width?: number; type?: string; labels?: string; settings?: string }) =>
  structureCall(b, 'columns', 'PATCH', { columnId, ...patch }, rev)

export const deleteColumn = (b: number | string, rev: number, columnId: number) =>
  structureCall(b, 'columns', 'DELETE', { columnId }, rev)
/** 复制分组：连带该组的条目与单元格值 */
export const duplicateGroup = (b: number | string, rev: number, groupId: number) =>
  structureCall(b, 'groups', 'POST', { duplicateOfGroupId: groupId }, rev)
/** 把条目移动到另一个分组 */
export const moveItem = (b: number | string, rev: number, itemId: number, toGroupId: number) =>
  request<{ ok: boolean; rev: number }>(`/api/boards/${b}/items/move`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ itemId, toGroupId }),
  })

/** 复制条目（连带单元格值）。注意响应键是 id */
export const duplicateItem = (b: number | string, rev: number, itemId: number) =>
  request<{ ok: boolean; rev: number; id: number }>(`/api/boards/${b}/items/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ itemId }),
  })
/** 在指定条目下方新建条目（服务端用 Pos 中点插到它后面） */
export const createItemBelow = (
  b: number | string, rev: number, groupId: number, name: string, afterItemId: number,
) =>
  request<{ ok: boolean; rev: number; id: number; itemId: number }>(`/api/boards/${b}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ name, groupId, afterItemId }),
  })
/** 列类型转换（M7）。dryRun=true 只统计 converted/cleared，不改写数据 */
export const convertColumnType = (
  b: number | string, rev: number, columnId: number, type: string, dryRun: boolean,
) =>
  request<{ ok: boolean; rev: number; dryRun: boolean; converted: number; cleared: number }>(
    `/api/boards/${b}/columns`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
      body: JSON.stringify({ columnId, type, convert: true, dryRun }),
    },
  )
/**
 * 更新标签表并重映射已有值（M7）。
 * plan = { remap: {旧索引: 新索引}, removed: [旧索引] }
 */
export const updateColumnLabels = (
  b: number | string, rev: number, columnId: number,
  labels: string, settings: string,
  plan: { remap: Record<string, string>; removed: string[] },
) =>
  request<{ ok: boolean; rev: number; moved: number; deleted: number }>(`/api/boards/${b}/columns`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ columnId, labels, settings, labelPlan: JSON.stringify(plan) }),
  })
/** 看板显示设置（M8）：只传要改的键，值为 null 表示删除 */
export const patchBoardSettings = (
  b: number | string, rev: number, patch: Record<string, unknown>,
) =>
  request<{ ok: boolean; rev: number; viewState: Record<string, unknown> }>(
    `/api/boards/${b}/settings`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
      body: JSON.stringify(patch),
    },
  )
/** 归档 / 取消归档看板（M8） */
export const archiveBoard = (b: number | string, rev: number, archived: boolean) =>
  request<{ ok: boolean; rev: number; archived: boolean }>(`/api/boards/${b}/archive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ archived }),
  })
/** 已归档条目（M8） */
export interface ArchivedItem {
  id: number
  name: string
  groupTitle: string
}

/** 列出已归档内容（M8） */
export const getArchive = (b: number | string) =>
  request<{ ok: boolean; items: ArchivedItem[]; groups: { id: number; title: string }[] }>(
    `/api/boards/${b}/archive`,
    { method: 'GET' },
  )

/** 归档 / 取消归档单个条目（M8） */
export const archiveItem = (b: number | string, rev: number, itemId: number, archived: boolean) =>
  request<{ ok: boolean; rev: number; archived: boolean }>(`/api/boards/${b}/items/archive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ itemId, archived }),
  })

/** 从归档里恢复一个条目（M8） */
export const restoreArchivedItem = (b: number | string, rev: number, itemId: number) =>
  archiveItem(b, rev, itemId, false)
/** 复制看板（M8）：深拷贝分组/列/条目/值，返回新看板 id */
export const copyBoard = (b: number | string) =>
  request<{ ok: boolean; boardId: number; rev: number }>(`/api/boards/${b}/copy`, {
    method: 'POST',
  })
/** 改看板名称（M8） */
export const renameBoard = (b: number | string, rev: number, name: string) =>
  request<{ ok: boolean; rev: number; name: string }>(`/api/boards/${b}/info`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
    body: JSON.stringify({ name }),
  })
/** 删除整个看板（M8）：级联删除分组/列/条目/值，不可恢复 */
export const deleteBoard = (b: number | string, rev: number) =>
  request<{ ok: boolean }>(`/api/boards/${b}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'If-Match': `"${rev}"` },
  })
// ===== 附件（M9.4 file 列）=====

export interface AttachmentInfo {
  id: number
  name: string
  size: number
  mime: string
}

/** 下载地址（直接给 <a href> 用） */
export function attachmentUrl(id: number): string {
  return `/api/attachments/${id}`
}

/**
 * 上传一个文件。
 * 用 XMLHttpRequest 而不是 fetch —— fetch 没有上传进度。
 * 服务端上传路由是独立的 SyncFileReceiver，返回 { ok, attachments:[…] }。
 */
export function uploadAttachment(
  boardId: number,
  itemId: number | undefined,
  columnKey: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AttachmentInfo> {
  return new Promise((resolve, reject) => {
    const query = new URLSearchParams()
    if (itemId !== undefined && itemId > 0) query.set('itemId', String(itemId))
    if (columnKey !== '') query.set('columnKey', columnKey)
    // 文件名显式上报：内核的临时文件表会把「原始文件名」沿用上一次的值（实测第二次上传仍叫上一次的名字）
    query.set('name', file.name)
    const url = `/api/boards/${boardId}/attachments/upload?${query.toString()}`

    // **不用 FormData**：Chrome 对 FormData 上传会用 `Transfer-Encoding: chunked`（内核实测 Content-Length
    // 为 size_t(-1)），而内核从不处理 chunked。这里手工拼 multipart 再以 Blob 发送，
    // 浏览器就能算出长度并带上 Content-Length。
    const boundary = `----friday${Math.random().toString(16).slice(2)}`
    // 真实文件名通过 query 的 name 参数传；这里的 filename 只求 ASCII 安全
    const head =
      `--${boundary}\r\n` +
      'Content-Disposition: form-data; name="file"; filename="upload.bin"\r\n' +
      'Content-Type: application/octet-stream\r\n\r\n'
    const tail = `\r\n--${boundary}--\r\n`
    const body = new Blob([head, file, tail])

    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Content-Type', `multipart/form-data; boundary=${boundary}`)
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable && e.total > 0) {
        onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)))
      }
    }
    xhr.onerror = () => reject(new ApiError(0, url, '网络错误'))
    xhr.onload = () => {
      let body: unknown = undefined
      try {
        body = JSON.parse(xhr.responseText) as unknown
      } catch {
        /* 非 JSON */
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const b = body as { message?: string; error?: string } | undefined
        reject(new ApiError(xhr.status, url, b?.message ?? b?.error ?? '上传失败', body))
        return
      }
      const list = (body as { attachments?: AttachmentInfo[] } | undefined)?.attachments ?? []
      // **不能盲取 attachments[0]**：服务端（内核）的临时文件表可能是跨请求累积的，
      // 一次响应里可能带回别的文件（实测第二次上传返回了上一次的 A）。
      // 这里按本次上传的文件名精确匹配，匹配不到时退回最后一项。
      const matched = list.find((a) => a.name === file.name) ?? list[list.length - 1]
      if (matched === undefined) {
        reject(new ApiError(xhr.status, url, '服务端没有返回附件信息', body))
        return
      }
      onProgress?.(100)
      resolve(matched)
    }
    xhr.send(body)
  })
}

/** 删除一个附件（服务端同时删磁盘文件） */
export async function deleteAttachment(id: number): Promise<void> {
  await request<{ ok: boolean }>(attachmentUrl(id), { method: 'DELETE' })
}
/** M9.8：保存组尾统计方式（落库到 AppSetting） */
export const setFooterAgg = (boardId: number | string, columnKey: string, agg: string) =>
  request<{ ok: boolean; columnKey: string; agg: string }>(`/api/boards/${boardId}/footer-agg`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ columnKey, agg }),
  })
// ===== M10：导入 =====

export interface BoardListItem {
  id: number
  name: string
}

export interface ImportReport {
  ok: boolean
  boardId?: number
  boardName?: string
  groupsCreated?: number
  groupsReused?: number
  columnsCreated?: number
  columnsReused?: number
  itemsCreated?: number
  valuesSkipped?: number
  message?: string
}

/** 看板清单（导入弹框选目标用） */
/** M11：新建空看板（后端 POST /api/boards） */
export const createBoard = (name: string) =>
  request<{ ok: boolean; boardId: number; rev: number; name: string }>('/api/boards', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })

/** backlog #10：孤儿附件（磁盘有、DB 没登记） */
export interface OrphanFile {
  storageName: string
  sizeBytes: number
  /** 'dir' = 空目录 */
  kind?: 'file' | 'dir'
}

export const listOrphanAttachments = () =>
  request<{ ok: boolean; dir: string; count: number; bytes: number; files: OrphanFile[] }>(
    '/api/attachments/orphans',
  )

export const purgeOrphanAttachments = () =>
  request<{ ok: boolean; dir: string; count: number; bytes: number; files: OrphanFile[]; removed: number; removedFiles: number; removedDirs: number }>(
    '/api/attachments/orphans/purge',
    { method: 'POST' },
  )

export const listBoards = () => request<{ ok: boolean; boards: BoardListItem[] }>('/api/boards-list')

/** 导入为新看板 */
export const importBoard = (payload: unknown) =>
  request<ImportReport>('/api/boards/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

/** 合并到现有看板 */
export const importIntoBoard = (boardId: number | string, payload: unknown) =>
  request<ImportReport>(`/api/boards/${boardId}/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
/** M10：写看板级 UI 设置（存 AppSetting，BoardID 作用域） */
export const setBoardAppSetting = (b: number | string, key: string, value: string) =>
  request<{ ok: boolean; key: string; value: string }>(`/api/boards/${b}/app-setting`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value }),
  })
